<script lang="ts" setup>
import { inject, ref, computed } from 'vue'
import CheckProductItemRenderer from './CheckProductItemRenderer.vue'
import CheckComboItemRenderer from './CheckComboItemRenderer.vue'
import CheckCustomComboItemRenderer from './CheckCustomComboItemRenderer.vue'
import type { Check } from '~/types/checks'

const { t, locale } = useI18n()
const dialogRef = inject('dialogRef') as any

// Store references
const tablesStore = useTablesStore()
const checksStore = useChecksStore()
const businessStore = useBusinessStore()
const { businessInfo } = storeToRefs(businessStore)

// Get check data from dialog
const checkData = computed(() => dialogRef?.value?.data?.checkData as Check)

// Email modal state
const showEmailDialog = ref(false)
const customerEmail = ref('')
const sendingEmail = ref(false)

// Edit state for open checks
const selectedTableId = ref<number | null>(null)
const selectedServerId = ref<number | null>(null)
const checkNotes = ref('')
const saving = ref(false)

// Initialize editable fields when dialog opens
onMounted(() => {
  if (checkData.value) {
    selectedTableId.value = checkData.value.table_id ?? null
    selectedServerId.value = checkData.value.server_id ?? null
    checkNotes.value = checkData.value.notes || ''
    customerEmail.value = ''
  }
})

// Computed
const isOpen = computed(() => checkData.value?.status === 'open')
const isClosed = computed(() => checkData.value?.status === 'closed')

// Get cart items with proper structure
const cartItems = computed(() => checkData.value?.cart || [])

// Calculate totals
const subtotal = computed(() => {
  return cartItems.value.reduce((sum, item) => sum + (item.total || 0), 0)
})

const tax = computed(() => checkData.value?.tax || 0)
const total = computed(() => checkData.value?.total || subtotal.value + tax.value)

// Get tables and servers for dropdowns
const { tables } = storeToRefs(tablesStore)
const activeTables = computed(() => tables.value.filter(t => t.active))

// Actions for OPEN checks
async function transferTable() {
  if (!selectedTableId.value || selectedTableId.value === checkData.value.table_id) {
    return
  }

  saving.value = true
  try {
    const success = await checksStore.updateCheck(checkData.value.id, {
      table_id: selectedTableId.value,
    })

    if (success) {
      useSuccessToast('Table transferred successfully')
      dialogRef.value.close()
    }
  }
  finally {
    saving.value = false
  }
}

async function saveNotes() {
  if (checkNotes.value === checkData.value.notes) {
    return
  }

  saving.value = true
  try {
    const success = await checksStore.updateCheck(checkData.value.id, {
      notes: checkNotes.value,
    })

    if (success) {
      useSuccessToast('Notes saved successfully')
    }
  }
  finally {
    saving.value = false
  }
}

async function voidCheck() {
  saving.value = true
  try {
    const success = await checksStore.updateStatus(checkData.value.id, 'cancelled')

    if (success) {
      useSuccessToast('Check voided successfully')
      dialogRef.value.close()
    }
  }
  finally {
    saving.value = false
  }
}

// Actions for CLOSED checks
function printReceipt() {
  window.print()
}

async function sendReceiptEmail() {
  if (!customerEmail.value) {
    useErrorToast('Please enter customer email')
    return
  }

  sendingEmail.value = true
  try {
    await $fetch('/api/email/send-receipt', {
      method: 'POST',
      body: {
        email: customerEmail.value,
        checkData: checkData.value,
        business: businessInfo.value,
        locale: locale.value,
      },
    })

    useSuccessToast('Email sent successfully')
    showEmailDialog.value = false
  }
  catch (error: any) {
    useErrorToast(error?.data?.message || 'Failed to send email')
  }
  finally {
    sendingEmail.value = false
  }
}

// Format date helper
function formatDate(dateString?: string) {
  if (!dateString)
    return '-'
  return new Date(dateString).toLocaleString(locale.value === 'es' ? 'es-MX' : 'en-US')
}

// Format currency helper
function formatCurrency(amount: number) {
  return `$${amount.toFixed(2)}`
}
</script>

<template>
  <div class="p-4">
    <!-- Two Column Layout -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- LEFT COLUMN: Cart Content -->
      <div class="space-y-4">
        <!-- Check Summary Header -->
        <div class="p-4 bg-gray-50 rounded-lg">
          <div class="flex items-center justify-between mb-2">
            <h3 class="font-semibold text-lg">
              #{{ checkData?.check_number?.slice(-4) }}
            </h3>
            <Tag
              :value="checkData?.status"
              :severity="checkData?.status === 'open' ? 'success' : 'secondary'"
            />
          </div>
          <div class="text-sm text-gray-600">
            <div>{{ t('Table') }}: {{ checkData?.table?.table_number || '-' }}</div>
            <div>{{ t('Created') }}: {{ formatDate(checkData?.opened_at) }}</div>
            <div v-if="isClosed">
              {{ t('Closed') }}: {{ formatDate(checkData?.closed_at) }}
            </div>
          </div>
        </div>

        <!-- Cart Items -->
        <div class="bg-white border border-gray-200 rounded-lg p-4">
          <h4 class="font-semibold mb-3">
            {{ t('Items') }}
          </h4>
          <div class="space-y-2 max-h-[400px] overflow-y-auto">
            <div v-if="cartItems.length > 0">
              <template
                v-for="item in cartItems"
                :key="item.cart_uuid"
              >
                <CheckProductItemRenderer
                  v-if="item.type === 'PRODUCT'"
                  :item="item"
                  :readonly="true"
                />
                <CheckComboItemRenderer
                  v-else-if="item.type === 'COMBO'"
                  :item="item"
                  :readonly="true"
                />
                <CheckCustomComboItemRenderer
                  v-else-if="item.type === 'CUSTOM_COMBO'"
                  :item="item"
                  :readonly="true"
                />
              </template>
            </div>
            <div
              v-else
              class="text-center py-8 text-gray-500"
            >
              <i class="pi pi-shopping-cart text-3xl mb-2" />
              <p class="text-sm">
                {{ t('No items in this check') }}
              </p>
            </div>
          </div>

          <!-- Totals -->
          <div class="border-t mt-4 pt-3 space-y-2">
            <div class="flex justify-between text-sm">
              <span>{{ t('Subtotal') }}</span>
              <span>{{ formatCurrency(subtotal) }}</span>
            </div>
            <div class="flex justify-between text-sm">
              <span>{{ t('Tax') }}</span>
              <span>{{ formatCurrency(tax) }}</span>
            </div>
            <div class="flex justify-between text-lg font-bold border-t pt-2">
              <span>{{ t('Total') }}</span>
              <span>{{ formatCurrency(total) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- RIGHT COLUMN: Actions -->
      <div class="space-y-4">
        <!-- OPEN CHECK ACTIONS -->
        <div
          v-if="isOpen"
          class="space-y-4"
        >
          <!-- Transfer Table -->
          <div class="bg-white border border-gray-200 rounded-lg p-4">
            <h4 class="font-semibold mb-3">
              {{ t('Transfer Table') }}
            </h4>
            <div class="flex gap-2">
              <Select
                v-model="selectedTableId"
                :options="activeTables"
                option-label="table_number"
                option-value="id"
                :placeholder="t('Select table')"
                class="flex-1"
              />
              <Button
                :label="t('Transfer')"
                icon="pi pi-arrow-right"
                :disabled="!selectedTableId || selectedTableId === checkData?.table_id || saving"
                :loading="saving"
                @click="transferTable"
              />
            </div>
          </div>

          <!-- Notes -->
          <div class="bg-white border border-gray-200 rounded-lg p-4">
            <h4 class="font-semibold mb-3">
              {{ t('Notes') }}
            </h4>
            <Textarea
              v-model="checkNotes"
              :placeholder="t('Add notes about this check')"
              rows="3"
              class="w-full"
            />
            <div class="flex justify-end mt-2">
              <Button
                :label="t('Save Notes')"
                icon="pi pi-save"
                size="small"
                :disabled="checkNotes === checkData?.notes || saving"
                :loading="saving"
                @click="saveNotes"
              />
            </div>
          </div>

          <!-- Void Check -->
          <div class="bg-white border border-gray-200 rounded-lg p-4">
            <h4 class="font-semibold mb-3 text-red-600">
              {{ t('Danger Zone') }}
            </h4>
            <Button
              :label="t('Void Check')"
              icon="pi pi-times"
              severity="danger"
              outlined
              :loading="saving"
              class="w-full"
              @click="voidCheck"
            />
          </div>
        </div>

        <!-- CLOSED CHECK ACTIONS -->
        <div
          v-if="isClosed"
          class="space-y-4"
        >
          <div class="bg-white border border-gray-200 rounded-lg p-4">
            <h4 class="font-semibold mb-3">
              {{ t('Receipt Actions') }}
            </h4>
            <div class="flex flex-col gap-2">
              <Button
                :label="t('Print')"
                icon="pi pi-print"
                outlined
                class="w-full"
                @click="printReceipt"
              />
              <Button
                :label="t('Send Email')"
                icon="pi pi-envelope"
                class="w-full"
                @click="showEmailDialog = true"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Email Dialog -->
    <Dialog
      v-model:visible="showEmailDialog"
      :header="t('Send Email')"
      modal
      :style="{ width: '400px' }"
    >
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium mb-2">{{ t('Customer Email') }}</label>
          <InputText
            v-model="customerEmail"
            type="email"
            :placeholder="t('Enter email address')"
            class="w-full"
          />
        </div>
        <div class="flex justify-end gap-2">
          <Button
            :label="t('Cancel')"
            severity="secondary"
            outlined
            @click="showEmailDialog = false"
          />
          <Button
            :label="t('Send')"
            icon="pi pi-send"
            :loading="sendingEmail"
            @click="sendReceiptEmail"
          />
        </div>
      </div>
    </Dialog>
  </div>
</template>
